import React, { useEffect, useState, useCallback } from 'react';
import client from '../api/client';

const PRIMARY = '#B71530';

function apiErr(err, fallback = 'Request failed') {
  const d = err?.response?.data?.detail;
  if (!d) return fallback;
  if (Array.isArray(d)) return d.map(x => x.msg || String(x)).join('; ');
  return String(d);
}

const COUNTRY_PRESETS = [
  { code: 'CN', name: 'China',        ranges: ['1.0.1.0/24','1.0.2.0/23','116.0.0.0/8'] },
  { code: 'RU', name: 'Russia',       ranges: ['2.56.0.0/13','5.8.0.0/16','45.8.0.0/16'] },
  { code: 'KP', name: 'North Korea',  ranges: ['175.45.176.0/22','210.52.109.0/24'] },
  { code: 'IR', name: 'Iran',         ranges: ['2.176.0.0/12','5.22.0.0/15','31.2.0.0/16'] },
];

export default function AppAdminSecurity() {
  const [tab, setTab]           = useState('ip');
  const [rules, setRules]       = useState([]);
  const [rateCfg, setRateCfg]   = useState({ threshold: 120, window_seconds: 60, auto_block: false });
  const [loading, setLoading]   = useState(true);
  const [msg, setMsg]           = useState({ text: '', type: 'success' });
  const [newRule, setNewRule]   = useState({ ip_cidr: '', rule_type: 'block', reason: '', country: '' });
  const [saving, setSaving]     = useState(false);

  const showMsg = (text, type = 'success') => {
    setMsg({ text, type });
    setTimeout(() => setMsg({ text: '', type: 'success' }), 4000);
  };

  const fetchAll = useCallback(() => {
    setLoading(true);
    Promise.all([
      client.get('/security/ip-rules'),
      client.get('/security/rate-limit'),
    ])
      .then(([ir, rl]) => {
        setRules(ir.data.rules || []);
        setRateCfg(rl.data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleAddRule = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const r = await client.post('/security/ip-rules', newRule);
      showMsg(r.data.message);
      setNewRule({ ip_cidr: '', rule_type: 'block', reason: '', country: '' });
      fetchAll();
    } catch (err) { showMsg(apiErr(err, 'Failed'), 'danger'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (rule) => {
    if (!window.confirm(`Remove rule for ${rule.ip_cidr}?`)) return;
    try {
      await client.delete(`/security/ip-rules/${rule.id}`);
      showMsg('Rule removed');
      fetchAll();
    } catch (err) { showMsg(apiErr(err), 'danger'); }
  };

  const handleToggle = async (rule) => {
    try {
      await client.patch(`/security/ip-rules/${rule.id}/toggle`);
      fetchAll();
    } catch (err) { showMsg(apiErr(err), 'danger'); }
  };

  const handleAddCountryPreset = async (preset) => {
    let added = 0;
    for (const range of preset.ranges) {
      try {
        await client.post('/security/ip-rules', {
          ip_cidr: range, rule_type: 'block',
          reason: `${preset.name} IP range`, country: preset.code,
        });
        added++;
      } catch { /* skip duplicates */ }
    }
    showMsg(`Added ${added} block rule(s) for ${preset.name}`);
    fetchAll();
  };

  const handleSaveRateLimit = async (e) => {
    e.preventDefault();
    try {
      await client.patch('/security/rate-limit', rateCfg);
      showMsg('Rate-limit settings saved');
    } catch (err) { showMsg(apiErr(err), 'danger'); }
  };

  const blocked = rules.filter(r => r.rule_type === 'block');
  const allowed = rules.filter(r => r.rule_type === 'allow');
  const autoBlocked = blocked.filter(r => r.auto_blocked);

  const TABS = [
    { id: 'ip',   label: `IP Rules (${rules.length})` },
    { id: 'rate', label: 'Rate Limiting' },
  ];

  return (
    <div>
      {msg.text && (
        <div className={`alert alert-${msg.type} py-2 mb-3`} style={{ fontSize: '13px' }}>{msg.text}</div>
      )}

      {/* Summary cards */}
      <div className="row mb-4">
        {[
          { label: 'Blocked IPs / Ranges', value: blocked.length,  color: '#dc3545' },
          { label: 'Allowed IPs / Ranges', value: allowed.length,  color: '#198754' },
          { label: 'Auto-blocked',          value: autoBlocked.length, color: '#fd7e14' },
        ].map(c => (
          <div className="col-md-4" key={c.label}>
            <div className="card text-center" style={{ borderLeft: `4px solid ${c.color}` }}>
              <div className="card-body py-3">
                <div style={{ fontSize: '28px', fontWeight: 700, color: c.color }}>{c.value}</div>
                <div style={{ fontSize: '12px', color: '#6c757d', textTransform: 'uppercase' }}>{c.label}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <ul className="nav app-tabs mb-4">
        {TABS.map(t => (
          <li className="nav-item" key={t.id}>
            <button
              className={`nav-link${tab === t.id ? ' active' : ''}`}
              onClick={() => setTab(t.id)}
            >
              {t.label}
            </button>
          </li>
        ))}
      </ul>

      {/* ── IP Rules tab ─────────────────────────────────────────────────── */}
      {tab === 'ip' && (
        <div>
          {/* Quick-block country presets */}
          <div className="card mb-4">
            <div className="card-header fw-semibold" style={{ fontSize: '13px', backgroundColor: '#fff8f8' }}>
              Quick Block by Country
              <span className="text-muted ml-2" style={{ fontWeight: 400, fontSize: '11px' }}>
                Add known high-risk IP ranges for entire countries
              </span>
            </div>
            <div className="card-body py-2 d-flex flex-wrap" style={{ gap: '8px' }}>
              {COUNTRY_PRESETS.map(p => (
                <button key={p.code} className="btn btn-sm btn-outline-danger"
                  style={{ fontSize: '12px' }}
                  onClick={() => handleAddCountryPreset(p)}>
                  🚫 Block {p.name} ({p.code})
                </button>
              ))}
              <span className="text-muted ml-2 align-self-center" style={{ fontSize: '11px' }}>
                Note: These are sample ranges. For full country CIDR lists, use a GeoIP database.
              </span>
            </div>
          </div>

          {/* Add custom rule */}
          <div className="card mb-4">
            <div className="card-header fw-semibold" style={{ fontSize: '13px', backgroundColor: '#f8f9fa' }}>
              Add IP Rule
            </div>
            <div className="card-body">
              <form onSubmit={handleAddRule}>
                <div className="row" style={{ gap: 0 }}>
                  <div className="col-md-3">
                    <label style={{ fontSize: '12px' }}>IP Address or CIDR</label>
                    <input type="text" className="form-control form-control-sm" required
                      value={newRule.ip_cidr}
                      onChange={e => setNewRule(p => ({ ...p, ip_cidr: e.target.value }))}
                      placeholder="e.g. 192.168.1.1 or 10.0.0.0/8" />
                  </div>
                  <div className="col-md-2">
                    <label style={{ fontSize: '12px' }}>Action</label>
                    <select className="form-control form-control-sm" value={newRule.rule_type}
                      onChange={e => setNewRule(p => ({ ...p, rule_type: e.target.value }))}>
                      <option value="block">🚫 Block</option>
                      <option value="allow">✅ Allow</option>
                    </select>
                  </div>
                  <div className="col-md-2">
                    <label style={{ fontSize: '12px' }}>Country (optional)</label>
                    <input type="text" className="form-control form-control-sm"
                      value={newRule.country} maxLength={2} placeholder="e.g. CN"
                      onChange={e => setNewRule(p => ({ ...p, country: e.target.value.toUpperCase() }))} />
                  </div>
                  <div className="col-md-3">
                    <label style={{ fontSize: '12px' }}>Reason</label>
                    <input type="text" className="form-control form-control-sm"
                      value={newRule.reason} placeholder="Why are you blocking this?"
                      onChange={e => setNewRule(p => ({ ...p, reason: e.target.value }))} />
                  </div>
                  <div className="col-md-2 d-flex align-items-end">
                    <button type="submit" className="btn btn-sm w-100"
                      style={{ backgroundColor: PRIMARY, color: '#fff', border: 'none' }}
                      disabled={saving}>
                      {saving ? '…' : 'Add Rule'}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>

          {/* Rules table */}
          <div className="card">
            <div className="card-header fw-semibold" style={{ fontSize: '13px', backgroundColor: '#f8f9fa' }}>
              Active Rules
            </div>
            <div className="card-body p-0">
              {loading ? (
                <div className="text-center py-4"><div className="spinner-border spinner-border-sm text-danger" /></div>
              ) : (
                <table className="table table-sm table-hover mb-0" style={{ fontSize: '12px' }}>
                  <thead className="table-light">
                    <tr><th>IP / CIDR</th><th>Action</th><th>Country</th><th>Reason</th><th>Source</th><th>Created</th><th>Status</th><th></th></tr>
                  </thead>
                  <tbody>
                    {rules.length === 0 && (
                      <tr><td colSpan="8" className="text-center text-muted py-4">No rules yet — the system is open to all IPs</td></tr>
                    )}
                    {rules.map(r => (
                      <tr key={r.id} style={{ opacity: r.active ? 1 : 0.5 }}>
                        <td><code style={{ fontSize: '12px' }}>{r.ip_cidr}</code></td>
                        <td>
                          <span style={{
                            padding: '1px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 600,
                            backgroundColor: r.rule_type === 'block' ? '#f8d7da' : '#d1e7dd',
                            color: r.rule_type === 'block' ? '#dc3545' : '#198754',
                          }}>
                            {r.rule_type === 'block' ? '🚫 Block' : '✅ Allow'}
                          </span>
                        </td>
                        <td>{r.country || '–'}</td>
                        <td style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#6c757d' }}>
                          {r.reason || '–'}
                        </td>
                        <td>
                          {r.auto_blocked
                            ? <span className="badge badge-warning" style={{ fontSize: '10px' }}>Auto</span>
                            : <span className="badge badge-secondary" style={{ fontSize: '10px' }}>Manual</span>}
                        </td>
                        <td style={{ color: '#6c757d' }}>
                          {r.created_at ? new Date(r.created_at).toLocaleDateString('en-GB') : '–'}
                        </td>
                        <td>
                          <span className={`badge ${r.active ? 'badge-success' : 'badge-secondary'}`} style={{ fontSize: '10px' }}>
                            {r.active ? 'Active' : 'Disabled'}
                          </span>
                        </td>
                        <td>
                          <div className="d-flex" style={{ gap: '4px' }}>
                            <button className="btn btn-sm btn-outline-secondary"
                              style={{ fontSize: '10px', padding: '1px 6px' }}
                              onClick={() => handleToggle(r)}>
                              {r.active ? 'Disable' : 'Enable'}
                            </button>
                            <button className="btn btn-sm btn-outline-danger"
                              style={{ fontSize: '10px', padding: '1px 6px' }}
                              onClick={() => handleDelete(r)}>
                              Remove
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Rate Limiting tab ────────────────────────────────────────────── */}
      {tab === 'rate' && (
        <div style={{ maxWidth: '520px' }}>
          <div className="card">
            <div className="card-header fw-semibold" style={{ fontSize: '13px', backgroundColor: '#f8f9fa' }}>
              Rate Limiting
            </div>
            <div className="card-body">
              <form onSubmit={handleSaveRateLimit}>
                <div className="mb-3">
                  <label className="form-label" style={{ fontSize: '13px' }}>
                    Request Threshold
                  </label>
                  <div className="d-flex align-items-center" style={{ gap: '8px' }}>
                    <input type="number" className="form-control form-control-sm"
                      style={{ maxWidth: '100px' }} min={1} max={10000}
                      value={rateCfg.threshold}
                      onChange={e => setRateCfg(p => ({ ...p, threshold: Number(e.target.value) }))} />
                    <span style={{ fontSize: '13px', color: '#495057' }}>requests per</span>
                    <input type="number" className="form-control form-control-sm"
                      style={{ maxWidth: '80px' }} min={1} max={3600}
                      value={rateCfg.window_seconds}
                      onChange={e => setRateCfg(p => ({ ...p, window_seconds: Number(e.target.value) }))} />
                    <span style={{ fontSize: '13px', color: '#495057' }}>seconds</span>
                  </div>
                  <div className="form-text" style={{ fontSize: '11px' }}>
                    An IP exceeding this rate receives HTTP 429 Too Many Requests
                  </div>
                </div>

                <div className="mb-4">
                  <div className="d-flex align-items-center" style={{ gap: '10px' }}>
                    <input type="checkbox" id="autoBlock" className="form-check-input"
                      checked={rateCfg.auto_block}
                      onChange={e => setRateCfg(p => ({ ...p, auto_block: e.target.checked }))} />
                    <label htmlFor="autoBlock" style={{ fontSize: '13px', cursor: 'pointer', margin: 0 }}>
                      Automatically add to IP block list when rate exceeded
                    </label>
                  </div>
                  <div className="form-text mt-1" style={{ fontSize: '11px', color: '#dc3545' }}>
                    ⚠ Use with caution — aggressive bots can share IPs with legitimate users (e.g. corporate proxies)
                  </div>
                </div>

                <button type="submit" className="btn btn-sm"
                  style={{ backgroundColor: PRIMARY, color: '#fff', border: 'none' }}>
                  Save Settings
                </button>
              </form>

              <hr style={{ margin: '20px 0' }} />

              <div>
                <h6 style={{ fontSize: '13px', fontWeight: 600 }}>How it works</h6>
                <ul style={{ fontSize: '12px', color: '#495057', paddingLeft: '16px', lineHeight: 1.8 }}>
                  <li>Every incoming request has its IP tracked in memory (no DB overhead)</li>
                  <li>If an IP sends more than <strong>{rateCfg.threshold}</strong> requests in <strong>{rateCfg.window_seconds}s</strong>, it gets a 429 response</li>
                  <li>With auto-block enabled, that IP is also added to the permanent block list</li>
                  <li>Blocked IPs can be reviewed and removed from the IP Rules tab</li>
                  <li>Health check <code>/api/health</code> is exempt and never rate-limited</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
